// sort-imports-ignore
import 'amazon-connect-streams';
import 'amazon-connect-chatjs';
import 'amazon-connect-taskjs';

import ReactDOM from 'react-dom';

import { browserTracingIntegration, metrics, replayIntegration, init as initSentry } from '@sentry/react';

import { getFormattedStackVersion } from '@cloud-wave/neon-common-lib';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';

import isDevEnvironment from 'lib/common/constants/isDevEnvironment';

import 'css/app.scss';
import 'css/global.scss';
// import '@cloud-wave/cloudwave-ui-kit/style.css';

import packageJson from '../package.json';
import App from './App';

export const STACK_VERSION = process.env.VITE_STACK_VERSION!;

// Guard against multiple initialisations by setting a window variable
if (!isDevEnvironment && !window.__SENTRY_INITIALIZED__) {
  window.__SENTRY_INITIALIZED__ = true;

  initSentry({
    environment: `${process.env.VITE_DEPLOYMENT_ENVIRONMENT}-${process.env.VITE_DEPLOY_REGION_CODE}`,
    dsn: 'https://a84b9050457b4b62b18b05cf7e8f5dc1@o1243134.ingest.sentry.io/6398215',
    integrations: [browserTracingIntegration(), replayIntegration(), metrics.metricsAggregatorIntegration()],
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    release: `${packageJson.name}@${getFormattedStackVersion(STACK_VERSION).presentation}`,
    normalizeDepth: 5,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 60 * 60 * 1000 * 24, // 24 hours
    },
    mutations: {
      retry: 1,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key: string) => Promise.resolve(window.localStorage.getItem(key)),
    setItem: (key: string, value: string) => Promise.resolve(window.localStorage.setItem(key, value)),
    removeItem: (key: string) => Promise.resolve(window.localStorage.removeItem(key)),
  },
  key: 'neon-app-query-cache',
  serialize: JSON.stringify,
  deserialize: JSON.parse,
});

const ReactApp = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 60 * 1000 * 1, // 1 minute
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist successful queries, not loading or error states
            return query.state.status === 'success' && query.state.dataUpdateCount > 0;
          },
        },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  );
};

ReactDOM.render(<ReactApp />, document.getElementById('root'));
